import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import whiteLabelApi from '../services/api/white-label.api';

interface WhiteLabelConfig {
  logo_url?: string;
  custom_name?: string;
  custom_url?: string;
}

interface WhiteLabelContextType {
  config: WhiteLabelConfig;
  loading: boolean;
  reload: () => Promise<void>;
  shouldShowWhiteLabel: boolean;
}

const WhiteLabelContext = createContext<WhiteLabelContextType | undefined>(undefined);

// Check if current hostname is a custom domain (not localhost, not default domain)
const isCustomDomain = (): boolean => {
  const hostname = window.location.hostname;
  const defaultDomains = ['localhost', '127.0.0.1', 'admin-flux.kubmix.com'];
  return !defaultDomains.includes(hostname);
};

export function WhiteLabelProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useSelector((state: RootState) => !!state.auth.token);
  const [config, setConfig] = useState<WhiteLabelConfig>({
    logo_url: undefined,
    custom_name: undefined,
    custom_url: undefined,
  });
  const [loading, setLoading] = useState(false);
  const [loadAttempted, setLoadAttempted] = useState(false);

  // White label should only show when:
  // 1. User is authenticated, OR
  // 2. Custom domain is being used
  const shouldShowWhiteLabel = isAuthenticated || isCustomDomain();

  const loadConfig = async (force = false) => {
    // Don't load if already attempted or if shouldn't show white label (unless forced)
    if (!force && (loadAttempted || !shouldShowWhiteLabel)) return;

    try {
      setLoading(true);
      setLoadAttempted(true);
      const response = await whiteLabelApi.getConfig();
      if (response.success && response.data) {
        setConfig({
          logo_url: response.data.logo_url || undefined,
          custom_name: response.data.custom_name || undefined,
          custom_url: response.data.custom_url || undefined,
        });
      }
    } catch (error) {
      console.error('[WhiteLabelContext] Error loading config:', error);
      // Keep default config on error - no need to do anything
    } finally {
      setLoading(false);
    }
  };

  // Load config when authentication state changes or on mount
  useEffect(() => {
    if (shouldShowWhiteLabel && !loadAttempted) {
      loadConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, shouldShowWhiteLabel]);

  // Update document title when custom_name changes and should show white label
  useEffect(() => {
    if (shouldShowWhiteLabel && config.custom_name) {
      document.title = config.custom_name;
    } else if (!shouldShowWhiteLabel) {
      document.title = 'Kubmix Flux - Observability Platform';
    }
  }, [config.custom_name, shouldShowWhiteLabel]);

  const reload = async () => {
    console.log('[WhiteLabelContext] Forcing reload...');
    await loadConfig(true); // Force reload bypassing checks
  };

  return (
    <WhiteLabelContext.Provider value={{ config, loading, reload, shouldShowWhiteLabel }}>
      {children}
    </WhiteLabelContext.Provider>
  );
}

export function useWhiteLabel() {
  const context = useContext(WhiteLabelContext);
  if (context === undefined) {
    throw new Error('useWhiteLabel must be used within a WhiteLabelProvider');
  }
  return context;
}
